import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { WebUxTitle } from '../WebUxContext';
import { Button, Input, Modal, Table, TableProps } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { useEventListener } from '../../common/EventContext';
import { useBusinessContext } from '../../context/BusinessContext';
import { StatusTag } from '../../common/StatusTag';
import {
  PettyCash,
  PettyCashItem,
  PettyCashItemStatus,
} from '../../dto/PettyCash';
import { Utils } from '../../common/Utils';
import { useNavigate, useParams } from 'react-router-dom';
import PettyCashItemEditModal, {
  usePettyCashItemEditModal,
} from './PettyCashItemEditModal';
import { useConnection } from '../../connection/connection';

type Props = {
  user: boolean;
  pettyCashId?: number;
};

export const PettyCashItemPage: FC<Props> = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<PettyCashItem[]>([]);
  const navigate = useNavigate();
  const connection = useConnection();
  const business = useBusinessContext();
  const [search, setSearch] = useState<string>();
  const [siteId, setSiteId] = useState<number>(0);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [name, setName] = useState<string>('');
  const modal = usePettyCashItemEditModal();
  const [pettyCashId, setPettyCashId] = useState<number>(0);
  const params = useParams<{
    pettyCashId: string;
  }>();
  const columns: any = useMemo(
    () => [
      {
        key: 'name',
        title: 'Name',
        dataIndex: 'name',
      },
      {
        key: 'notes',
        title: 'Notes',
        dataIndex: 'notes',
      },
      {
        key: 'site',
        title: 'Site',
        dataIndex: 'siteId',
        render: (r: string, record: any) => business.sites[r].name,
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        render: (text: string) => (
          <StatusTag
            options={{ Approved: 'green', Declined: 'red', Pending: 'orange' }}
            value={text}
          />
        ),
      },
      {
        key: 'amount',
        title: 'Amount',
        dataIndex: 'amount',
        render: (text: number) => Utils.formatPrice(text ?? 0),
      },
      {
        key: 'user',
        title: 'User',
        dataIndex: 'userId',
        render: (text: number) => Utils.formatPrice(text ?? 0),
      },
      {
        key: 'actions',
        title: '',
        dataIndex: 'id',
        render: (text: string, record: any) => (
          <>
            {(!props.user ||
              (props.user &&
                record.status === PettyCashItemStatus.Pending)) && (
              <Button
                type="link"
                onClick={() =>
                  modal.current?.edit(
                    record.pettyCashId,
                    record.pettyCashItemId,
                  )
                }
              >
                <EditOutlined />
              </Button>
            )}
          </>
        ),
      },
    ],
    [business.sites, modal, props.user],
  );
  useEffect(() => {
    setLoading(true);
    connection
      .get<{ pettyCash: PettyCash; pettyCashItems: PettyCashItem[] }>(
        `${business.businessId}/${props.user ? props.pettyCashId : params.pettyCashId}/petty-cash-item`,
      )
      .then((res) => {
        setName(res.data.pettyCash.name);
        setItems(
          res.data.pettyCashItems.map((e) => ({
            ...e,
            key: e.pettyCashItemId,
          })),
        );
        setPettyCashId(res.data.pettyCash.pettyCashId);
      })
      .finally(() => setLoading(false));
  }, [
    business.businessId,
    connection,
    params.pettyCashId,
    props.pettyCashId,
    props.user,
  ]);

  useEventListener(
    'PETTYCASHITEM',
    useCallback((dt) => {
      const item: PettyCashItem = dt.data;
      if (dt.action === 'CREATE') {
        setItems((prevState) => [item, ...prevState]);
      } else if (dt.action === 'UPDATE') {
        setItems((prevState) =>
          prevState.map((a) =>
            a.pettyCashItemId === item.pettyCashItemId ? item : a,
          ),
        );
      }
    }, []),
  );

  const statusChangeOnSelected = useCallback(() => {
    if (selectedIds.length > 0) {
      setLoading(true);
      connection
        .patch<PettyCashItem>(
          `${business.businessId}/${props.user ? pettyCashId : params.pettyCashId}/petty-cash-item/multiple-approve`,
          { ids: selectedIds },
        )
        .then((res) => {
          setItems(
            items.map((e) =>
              selectedIds.includes(e.pettyCashItemId)
                ? { ...e, status: PettyCashItemStatus.Approved }
                : e,
            ),
          );
        })
        .catch((err) => {
          Modal.error({
            title: 'Error',
            content: 'Something went wrong. Please try again later.',
          });
        })
        .finally(() => setLoading(false));
    }
  }, [
    business.businessId,
    connection,
    items,
    params.pettyCashId,
    pettyCashId,
    props.user,
    selectedIds,
  ]);
  const warehouses = useMemo(() => {
    const s = (search ?? '').trim().toLowerCase();
    const b = siteId === 0 ? undefined : siteId;
    return items
      .map((e) => ({ ...e, key: e.pettyCashItemId }))
      .filter(
        (item) =>
          !search ||
          search.trim() === '' ||
          item.name.toLowerCase().includes(s),
      )
      .filter((item) => !b || b === item.siteId);
  }, [items, search, siteId]);

  return (
    <div className="flex flex-col gap-2">
      <WebUxTitle>
        {props.user ? (
          'Petty Cash Account'
        ) : (
          <>
            <Button type={'link'} onClick={() => navigate('/webux/petty-cash')}>
              <ArrowLeftOutlined />
            </Button>
            {name} Petty Cash Account Items
          </>
        )}
      </WebUxTitle>
      <div className="flex gap-1 justify-between">
        <div className="flex gap-1">
          <div className="w-[500px]">
            <Input.Search
              onSearch={setSearch}
              allowClear={true}
              className="w-full"
              placeholder="Search Petty cash by name"
            />
          </div>
        </div>
        <div className="flex gap-1 justify-between">
          {!props.user && (
            <Button
              type="primary"
              style={{
                backgroundColor: 'green',
                color: 'white',
                opacity: selectedIds.length === 0 ? 0.6 : 1,
              }}
              disabled={selectedIds.length === 0}
              onClick={() => statusChangeOnSelected()}
            >
              Approve
            </Button>
          )}
          <Button
            type="primary"
            onClick={() =>
              modal.current?.create(+(params?.pettyCashId ?? pettyCashId))
            }
          >
            Create Item
          </Button>
        </div>
      </div>
      <Table
        size="small"
        rowSelection={
          props.user
            ? undefined
            : {
                type: 'checkbox',
                selectedRowKeys: selectedIds,
                onChange: (
                  selectedRowKeys: React.Key[],
                  selectedRows: PettyCashItem[],
                ) => {
                  console.log(
                    `selectedRowKeys: ${selectedRowKeys}`,
                    'selectedRows: ',
                    selectedRows,
                  );
                  setSelectedIds(selectedRows.map((e) => e.pettyCashItemId));
                },
                getCheckboxProps: (record: PettyCashItem) => ({
                  disabled:
                    record.status === PettyCashItemStatus.Declined ||
                    record.status === PettyCashItemStatus.Approved, // Column configuration not to be checked
                  name: record.name,
                }),
              }
        }
        loading={loading}
        pagination={false}
        columns={columns}
        dataSource={warehouses}
      />
      <PettyCashItemEditModal ref={modal} user={props.user} />
    </div>
  );
};
